import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { BoxProgramDetailsEntity } from "@/modules/boxProgrammation/entities/boxProgramEntity"
import { BoxSlotFormikEntity } from "@/modules/boxSlot/entities/boxSlotFormikEntity"
import { useFetchBoxSlotDetails } from "@/modules/boxSlot/hooks/useBoxSlotDetails"
import { useBoxSlotsUpdate } from "@/modules/boxSlot/hooks/useBoxSlots"
import { BoxSessionDetailsEntity } from "@/modules/boxSlotSession/entities/boxSessionEntity"
import { BoxSessionFormikEntity } from "@/modules/boxSlotSession/entities/boxSessionFormikEntity"
import { getBoxSessionValidationSchema } from "@/modules/boxSlotSession/formik/boxSessionFormikTypes"
import { useBoxSlotCreateSession } from "@/modules/boxSlotSession/hooks/useBoxSlotCreateSession"
import { useBoxSlotUpdateSession } from "@/modules/boxSlotSession/hooks/useBoxSlotUpdateSession"
import { word } from "@/modules/core/utils/i18n"
import { useFormik } from "formik"
import { useCallback, useState } from "react"

export const useBoxProgrammationSessionFormik = ({
  boxProgram,
  editedSession,
  dateSession,
  editedBoxSlotId,
}: {
  boxProgram: BoxProgramDetailsEntity
  editedSession?: BoxSessionDetailsEntity
  dateSession?: string | number
  editedBoxSlotId?: string
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const { mutateAsync: updateBoxSlotSession } = useBoxSlotUpdateSession()
  const { mutateAsync: createBoxSlotSession } = useBoxSlotCreateSession()
  const { mutateAsync: updateSlot } = useBoxSlotsUpdate()
  const { fetchData: fetchBoxSlotDetails } = useFetchBoxSlotDetails()

  const getInitialValues = useCallback(() => {
    if (editedSession) {
      return BoxSessionFormikEntity.fromEntityToFormik(editedSession, dateSession ? new Date(dateSession) : new Date())
    } else {
      return BoxSessionFormikEntity.getDraftFormik(dateSession ? new Date(dateSession) : new Date())
    }
  }, [editedSession, boxProgram.id, dateSession])

  const sessionFormSubmitHandler = async (sessionFormikValues: BoxSessionFormikEntity) => {
    if (editedSession) {
      await updateBoxSlotSession({
        sessionId: editedSession.id,
        sessionUpdateInputDto: sessionFormikValues.toUpdateInputDto(),
      })
    } else {
      if (!editedBoxSlotId) throw new Error("editedBoxSlotId is required")
      const editedBoxSlot = await fetchBoxSlotDetails(editedBoxSlotId)
      if (!editedBoxSlot) throw new Error("editedBoxSlot not found")
      const boxSession = await createBoxSlotSession({
        sessionCreateInputDto: sessionFormikValues.toCreateInputDto(boxProgram.id),
      })
      await updateSlot({
        slotId: editedBoxSlot.id,
        slotInput: { ...BoxSlotFormikEntity.fromEntity(editedBoxSlot).toInputDto(), sessionId: boxSession.id },
      })
    }
  }

  const formik = useFormik<BoxSessionFormikEntity>({
    initialValues: getInitialValues(),
    validationSchema: getBoxSessionValidationSchema,
    onSubmit: async () => {
      setIsSubmitting(true)

      try {
        await sessionFormSubmitHandler(formik.values)
        setIsSubmitting(false)
        toast.success(editedSession ? word("program.session.update.success") : word("program.session.create.success"), {
          classNames: successToastClassName(),
        })
        return true
      } catch (e) {
        setIsSubmitting(false)
        console.error("Session form submit error", e)
        toast.error(word("global.error.default"), {
          classNames: errorToastClassName(),
        })
        return false
      }
    },
  })

  return { formik, isSubmitting }
}
